import i18n from "@/core/plugins/vue-i18n";

// Accept both region codes ("de-de") and bare language codes ("de"). Backend
// select option labels are keyed by bare language (e.g. {"de":"…","en":"…"}).
const LOCALE_KEY_RE = /^[a-z]{2}(-[a-z]{2})?$/i;

function isLocaleMap(obj) {
  const keys = Object.keys(obj);
  return keys.length > 0 && keys.every(k => LOCALE_KEY_RE.test(k));
}

/**
 * True only for a map of locale code to string — the shape a backend option
 * label uses when it carries one text per language.
 *
 * Narrower than "is an object" on purpose: a caller uses this to decide whether
 * resolving is safe, and `resolveLocalizedString` answers "" for an object that
 * is not a locale map, which would blank a label rather than leave it alone.
 */
export function isLocalizedStringMap(value) {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    isLocaleMap(value)
  );
}

// Pick a sensible fallback value for backfilling missing locale slots.
function pickFallback(values) {
  if (values["en-us"]) return values["en-us"];
  if (values["de-de"]) return values["de-de"];
  for (const v of Object.values(values)) {
    if (v) return v;
  }
  return "";
}

// Ensure every known locale slot has a value so edit forms never show empty
// inputs when a flow was saved with only a subset of locales populated.
function backfillKnownLocales(values) {
  const fallback = pickFallback(values);
  if (!fallback) return values;
  const filled = { ...values };
  for (const { key } of getLocalizedStringLocales()) {
    if (filled[key]) continue;
    // Prefer a same-language value (e.g. backfill "en-us" from a bare "en")
    // so a {"de":…,"en":…} map doesn't fill the EN slot with the DE fallback.
    const lang = key.split("-")[0];
    const langMatch = Object.keys(values).find(
      k => k === lang || k.startsWith(lang + "-")
    );
    filled[key] = langMatch ? values[langMatch] : fallback;
  }
  return filled;
}

/**
 * Parse a raw value that may be a JSON localized object or a legacy plain string.
 * Returns an object keyed by locale code with every known locale populated
 * (missing slots are backfilled from `en-us`/`de-de`/first available value).
 * Non-locale objects return {}.
 */
export function parseLocalizedString(raw) {
  if (!raw) return {};

  if (typeof raw === "object" && !Array.isArray(raw)) {
    return isLocaleMap(raw) ? backfillKnownLocales(raw) : {};
  }

  if (typeof raw !== "string") return {};

  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      isLocaleMap(parsed)
    ) {
      return backfillKnownLocales(parsed);
    }
  } catch {
    // Legacy plain string — fall through
  }

  // Legacy plain string: replicate into every known locale so edit forms
  // pre-populate every slot until the user overrides one.
  const seeded = {};
  for (const { key } of getLocalizedStringLocales()) {
    seeded[key] = raw;
  }
  return seeded;
}

/**
 * Resolve a raw localized string to the text for the current locale.
 * Fallback chain:
 *   1. Exact locale match (e.g. "de-de" → values["de-de"])
 *   2. Language-prefix match (e.g. locale "de" → first stored key starting
 *      with "de-", so "de-de" still resolves when i18n.locale is bare lang)
 *   3. "en-us" default
 *   4. "de-de" default
 *   5. First available value
 *   6. ""
 */
export function resolveLocalizedString(raw) {
  if (!raw) return "";

  const values = parseLocalizedString(raw);

  // Non-locale input (e.g. structured JSON message): leave the raw value alone.
  if (Object.keys(values).length === 0) {
    return typeof raw === "string" ? raw : "";
  }

  const locale = i18n.locale;
  const lang = locale.split("-")[0];

  if (values[locale]) return values[locale];

  const prefixKey = Object.keys(values).find(
    key => key === lang || key.startsWith(lang + "-")
  );
  if (prefixKey && values[prefixKey]) return values[prefixKey];

  if (values["en-us"]) return values["en-us"];
  if (values["de-de"]) return values["de-de"];

  const firstValue = Object.values(values)[0];
  return firstValue ?? "";
}

const EMBEDDED_LOCALIZED_REGEX =
  /\{\s*"[a-z]{2}-[a-z]{2}"\s*:\s*"(?:[^"\\]|\\.)*"(?:\s*,\s*"[a-z]{2}-[a-z]{2}"\s*:\s*"(?:[^"\\]|\\.)*")*\s*\}/gi;

/**
 * Replace any embedded localized JSON blobs inside a free-form string with the
 * current-locale value. Used for backend-rendered messages that interpolate
 * raw localized name JSON (e.g. log/journal messages).
 */
export function resolveEmbeddedLocalizedStrings(text) {
  if (typeof text !== "string" || !text) return text ?? "";
  return text.replace(EMBEDDED_LOCALIZED_REGEX, match =>
    resolveLocalizedString(match)
  );
}

/**
 * Build a localized JSON string by updating one locale slot in the existing structure.
 */
export function buildLocalizedString(existingRaw, newText, locale) {
  const values = parseLocalizedString(existingRaw);
  const targetLocale = locale ?? i18n.locale;

  values[targetLocale] = newText;

  return JSON.stringify(values);
}

/**
 * Get available locales with labels and flag paths.
 */
export function getLocalizedStringLocales() {
  return [
    {
      key: "en-us",
      label: "English",
      flag: process.env.BASE_URL + "media/svg/flags/260-united-kingdom.svg"
    },
    {
      key: "de-de",
      label: "Deutsch",
      flag: process.env.BASE_URL + "media/svg/flags/162-germany.svg"
    }
  ];
}
